import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { msalInstance } from './auth/authConfig';
import { AuthProvider } from './auth/AuthContext';
import { ToastProvider } from './components/ui/ToastProvider';
import { GlobalDataProvider } from './contexts/GlobalDataContext';
import { LanguageProvider } from './contexts/LanguageContext';
import Layout from './components/layout/Layout';
import Login from './components/auth/Login';
import AuthRedirect from './components/auth/AuthRedirect';
import RootRoute from './components/routes/RootRoute';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ProtectedSupplierRoute from './components/auth/ProtectedSupplierRoute'; // **HINZUGEFÜGT**
import Dashboard from './pages/Dashboard';
import Questionnaires from './pages/Questionnaires';
import QuestionnaireEditor from './components/questionnaires/QuestionnaireEditor';
import Submissions from './pages/Submissions';
import Suppliers from './pages/Suppliers';
import Analytics from './pages/Analytics';
import WorkflowManagement from './pages/WorkflowManagement';
import Articles from './pages/Articles';
import Settings from './pages/Settings';
import BrandManagement from './pages/BrandManagement';
import DataExport from './pages/DataExport';
import MasterArticleManagement from './components/admin/MasterArticleManagement';
import AuctionManagement from './components/admin/AuctionManagement';

// **SUPPLIER PORTAL COMPONENTS**
import SupplierQuestionnaire from './pages/supplier/SupplierQuestionnaire';
import SupplierPortalDashboard from './pages/supplier/SupplierPortalDashboard';
import SupplierQuestionnaires from './pages/supplier/SupplierQuestionnaires';
import SupplierArticles from './pages/supplier/SupplierArticles';
import SupplierAuctions from './pages/supplier/SupplierAuctions';
import SupplierAuctionDetail from './pages/supplier/SupplierAuctionDetail';
import GeneralPortalLogin from './pages/supplier/GeneralPortalLogin';
import PasswordResetPage from './pages/supplier/PasswordResetPage';
import ChangePasswordPage from './pages/supplier/ChangePasswordPage';
import SupplierQuestionnaireSuccess from './pages/supplier/SupplierQuestionnaireSuccess';

function App() {
  // MsalProvider will handle MSAL initialization automatically
  return (
    <MsalProvider instance={msalInstance}>
      <AuthProvider>
        <ToastProvider>
          <GlobalDataProvider>
            <LanguageProvider>
              <Router>
                <Routes>
                {/* **SUPPLIER PORTAL ROUTES - MÜSSEN VOR ADMIN ROUTEN STEHEN** */}
                
                {/* Supplier Portal Root - Allgemeine Anmeldung */}
                <Route 
                  path="/supplier" 
                  element={<Navigate to="/supplier/portal" replace />} 
                />
                
                {/* Supplier Portal Index - Zeigt allgemeine Anmeldung */}
                <Route 
                  path="/supplier/portal" 
                  element={<GeneralPortalLogin />} 
                />
                
                {/* Supplier Portal mit spezifischem Token */}
                <Route 
                  path="/supplier/portal/:masterToken" 
                  element={<ProtectedSupplierRoute />} 
                />
                
                {/* Supplier Portal Login */}
                <Route 
                  path="/supplier/portal/:masterToken/login" 
                  element={<GeneralPortalLogin />} 
                />
                
                {/* Supplier Portal Dashboard */}
                <Route 
                  path="/supplier/portal/:masterToken/dashboard" 
                  element={<ProtectedSupplierRoute />} 
                />
                
                {/* Supplier Portal Questionnaires */}
                <Route 
                  path="/supplier/portal/:masterToken/questionnaires" 
                  element={<ProtectedSupplierRoute />} 
                />
                
                {/* Supplier Portal Articles */}
                <Route 
                  path="/supplier/portal/:masterToken/articles" 
                  element={<ProtectedSupplierRoute />} 
                />
                
                {/* Supplier Portal Auctions */}
                <Route 
                  path="/supplier/portal/:masterToken/auctions" 
                  element={<ProtectedSupplierRoute />} 
                />
                
                {/* Supplier Portal Auction Detail */}
                <Route 
                  path="/supplier/portal/:masterToken/auctions/:auctionId" 
                  element={<ProtectedSupplierRoute />} 
                />
                
                {/* Supplier Portal Questionnaire Success */}
                <Route 
                  path="/supplier/portal/:masterToken/questionnaire/success" 
                  element={<SupplierQuestionnaireSuccess />} 
                />
                
                {/* Supplier Portal Password Management */}
                <Route 
                  path="/supplier/portal/:masterToken/change-password" 
                  element={<ChangePasswordPage />} 
                />
                <Route 
                  path="/supplier/portal/:masterToken/password-reset" 
                  element={<PasswordResetPage />} 
                />
                
                {/* Questionnaire Routes (Independent from Portal) */}
                <Route 
                  path="/questionnaire/:accessToken" 
                  element={<SupplierQuestionnaire />} 
                />
                <Route 
                  path="/questionnaire/:accessToken/success" 
                  element={<SupplierQuestionnaireSuccess />} 
                />
                
                {/* **ADMIN ROUTES - NACH SUPPLIER ROUTES** */}
                <Route path="/" element={<RootRoute />} />
                <Route path="/login" element={<Login />} />
                <Route path="/auth-redirect" element={<AuthRedirect />} />
                
                {/* Protected Admin Routes */}
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                
                <Route path="/questionnaires" element={
                  <ProtectedRoute>
                    <Layout>
                      <Questionnaires />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/questionnaires/create" element={
                  <ProtectedRoute>
                    <Layout>
                      <QuestionnaireEditor />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/questionnaires/edit/:id" element={
                  <ProtectedRoute>
                    <Layout>
                      <QuestionnaireEditor />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/submissions" element={
                  <ProtectedRoute>
                    <Layout>
                      <Submissions />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/suppliers" element={
                  <ProtectedRoute>
                    <Layout>
                      <Suppliers />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/articles" element={
                  <ProtectedRoute>
                    <Layout>
                      <Articles />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/master-articles" element={
                  <ProtectedRoute>
                    <Layout>
                      <MasterArticleManagement />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/auctions" element={
                  <ProtectedRoute>
                    <Layout>
                      <AuctionManagement />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/analytics" element={
                  <ProtectedRoute>
                    <Layout>
                      <Analytics />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/workflows" element={
                  <ProtectedRoute>
                    <Layout>
                      <WorkflowManagement />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/brands" element={
                  <ProtectedRoute>
                    <Layout>
                      <BrandManagement />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/export" element={
                  <ProtectedRoute>
                    <Layout>
                      <DataExport />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <Layout>
                      <Settings />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                {/* Catch all - ABER NUR FÜR ADMIN ROUTEN */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Router>
            </LanguageProvider>
          </GlobalDataProvider>
        </ToastProvider>
      </AuthProvider>
    </MsalProvider>
  );
}

export default App;